// Nummer wählen: Telefon-Symbol in der Kopfzeile, darunter ein Ziffernfeld mit Eingabefeld. Einfügen (auch „0176 …“, „0049 …“,
// „+49 (0) …“) wird zu +49 und DE-Gruppierung, Löschen und Leeren gehen per Knopf. Gehört die Nummer zu einem Lead, steht
// sein Name da und der Anruf wird ihm zugeordnet; sonst ruft das CRM ohne Lead an (die API verlangt dafür heute noch eine
// Freigabe, siehe AENDERUNGSWUENSCHE.md „Anrufleiste“). Absendernummer: wie im Headset-Menü gewählt. Grüner Knopf = Anruf,
// keine Rückfrage. Läuft ein Power Dialer, wird er pausiert, damit der Countdown nicht dazwischen wählt.
import { useEffect, useMemo, useRef, useState } from "react";
import { sucheApi } from "../api";
import { Icon } from "../components/Icon";
import { Ausklapp } from "../components/Ausklapp";
import { modTaste } from "../werkzeuge/tasten";
import { nummerFormat, nummerNormalisieren, nummerZumWaehlen } from "./logik";
import { useTelefonie } from "./TelefonieProvider";
import "./telefonie.css";

interface LeadTreffer {
  leadId: string;
  contactId: string | null;
  lead: string;
  kontakt: string | null;
}

const PAD = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "0", "⌫"];

function WahlFeld({ onFertig }: { onFertig: () => void }) {
  const t = useTelefonie();
  const [text, setText] = useState("");
  const [treffer, setTreffer] = useState<LeadTreffer[] | null>(null);
  const [gewaehlt, setGewaehlt] = useState(0);
  const eingabe = useRef<HTMLInputElement>(null);
  const ziel = useMemo(() => nummerZumWaehlen(text), [text]);

  useEffect(() => {
    setTreffer(null);
    setGewaehlt(0);
    if (!ziel) return;
    const abbruch = new AbortController();
    const timer = window.setTimeout(() => {
      sucheApi
        .suchen(ziel, 8, abbruch.signal)
        .then((r) => {
          const gesehen = new Set<string>();
          const liste: LeadTreffer[] = [];
          for (const x of r.data) {
            if (x.match !== "phone" || gesehen.has(x.lead_id)) continue;
            gesehen.add(x.lead_id);
            // Bei Kontakt-Treffern steht in subtitle der Lead
            liste.push({
              leadId: x.lead_id,
              contactId: x.contact_id,
              lead: x.type === "contact" ? (x.subtitle ?? x.title) : x.title,
              kontakt: x.type === "contact" ? x.title : x.subtitle,
            });
          }
          setTreffer(liste.slice(0, 4));
        })
        .catch(() => {
          if (!abbruch.signal.aborted) setTreffer([]);
        });
    }, 250);
    return () => {
      window.clearTimeout(timer);
      abbruch.abort();
    };
  }, [ziel]);

  const imAnruf = !!t.aktiverAnruf;
  const nichtBereit = t.status === "nicht_eingerichtet" || t.status === "initialisiert";
  const sucht = !!ziel && treffer === null;
  const kannAnrufen = !!ziel && !sucht && !imAnruf && !nichtBereit;
  const z = treffer?.[gewaehlt] ?? null;

  const anrufen = () => {
    if (!kannAnrufen || !ziel) return;
    const d = t.dialer;
    if (d && !d.sitzung.beendet && !d.pausiert) t.dialerPause(true);
    onFertig();
    void t.anrufen(ziel, z?.leadId ?? "", z?.contactId ?? undefined, { leadName: z?.lead ?? null, kontaktName: z?.kontakt ?? null });
  };

  const setzen = (neu: string) => setText(nummerNormalisieren(neu));
  const absender = t.info?.caller_id ?? null;

  return (
    <div className="tel-wahl" data-tel-wahl="">
      <div className="tel-wahl__feld">
        <input
          ref={eingabe}
          className="eingabe tel-wahl__eingabe"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          autoFocus
          aria-label="Telefonnummer"
          placeholder="Nummer eingeben oder einfügen"
          value={nummerFormat(text)}
          onChange={(e) => setzen(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              anrufen();
            }
          }}
        />
        <button type="button" className="tel-symbolknopf" title="Leeren" aria-label="Nummer leeren" disabled={!text} onClick={() => { setText(""); eingabe.current?.focus(); }}>
          <Icon name="schliessen" groesse={14} />
        </button>
      </div>
      <div className="tel-wahl__lead" aria-live="polite" data-tel-wahl-lead="">
        {!ziel ? (
          <span>&nbsp;</span>
        ) : sucht ? (
          <span>Suche …</span>
        ) : z ? (
          <>
            <span>
              Lead: <strong>{z.lead}</strong>
              {z.kontakt && z.kontakt !== z.lead ? ` (${z.kontakt})` : ""}
            </span>
            {treffer && treffer.length > 1 ? (
              <div className="tel-wahl__wahlen">
                {treffer.map((x, i) => (
                  <button key={`${x.leadId}-${x.contactId ?? ""}`} type="button" className="tel-wahl__chip" aria-pressed={i === gewaehlt} onClick={() => setGewaehlt(i)}>
                    {x.lead}
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <span>Kein Lead mit dieser Nummer: Anruf ohne Lead</span>
        )}
      </div>
      <div className="tel-wahl__pad">
        {PAD.map((k) => (
          <button
            key={k}
            type="button"
            className="tel-taste"
            aria-label={k === "⌫" ? "Letzte Ziffer löschen" : k === "+" ? "Plus" : `Ziffer ${k}`}
            onClick={() => {
              if (k === "⌫") setText((x) => nummerNormalisieren(x.slice(0, -1)));
              else setText((x) => nummerNormalisieren(x + k));
              eingabe.current?.focus();
            }}
          >
            {k}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="tel-wahl__anrufen"
        disabled={!kannAnrufen}
        title={imAnruf ? "Es läuft schon ein Anruf." : nichtBereit ? (t.grund ?? "Telefonie ist nicht bereit.") : "Anrufen"}
        onClick={anrufen}
      >
        <Icon name="telefon" groesse={15} />
        Anrufen
      </button>
      <div className="tel-klein">{absender ? `Von ${nummerFormat(absender)} (Absender im Headset-Menü wählbar)` : "Keine Absendernummer hinterlegt"}</div>
    </div>
  );
}

export function NummernWahlKnopf() {
  const t = useTelefonie();
  const [anker, setAnker] = useState<HTMLButtonElement | null>(null);
  const [offen, setOffen] = useState(false);
  const aus = t.status === "nicht_eingerichtet";

  // ⌘⇧Y öffnet und schließt das Ziffernfeld (wirkt auch beim Tippen)
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || !(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey || e.key.toLowerCase() !== "y") return;
      e.preventDefault();
      setOffen((o) => !o);
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, []);

  return (
    <>
      <button
        ref={setAnker}
        type="button"
        className="symbolknopf"
        title={aus ? (t.grund ?? "Telefonie ist nicht eingerichtet.") : `Nummer wählen (${modTaste}⇧Y)`}
        aria-label="Nummer wählen"
        aria-haspopup="dialog"
        aria-expanded={offen}
        disabled={aus}
        data-tel-nummer-wahl=""
        onClick={() => setOffen((o) => !o)}
      >
        <Icon name="telefon" groesse={16} />
      </button>
      <Ausklapp anker={anker} offen={offen && !aus} onSchliessen={() => setOffen(false)} ausrichtung="end" breite={272} maxHoehe={620} rolle="dialog">
        <WahlFeld onFertig={() => setOffen(false)} />
      </Ausklapp>
    </>
  );
}
